/**
 * Add User Form Hook - Clean Architecture Version
 *
 * Custom hook for managing add user form state and business logic.
 * Follows clean architecture principles with proper separation of concerns.
 * Separates business logic from UI components.
 *
 * @module useAddUserForm
 */

import {useEffect, useRef, useCallback, useMemo} from 'react';
import {useFormState, CommonValidationRules, FormState, FormActions} from '../../../Presentation/utils/formUtils';
import {CreateUserRequest, UserStatus} from '../models/user.model';
import {useRoles} from './useRoles';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface UseAddUserFormOptions {
  open?: boolean;
  onSuccess?: (user: any) => void;
  onSubmit?: (userData: CreateUserRequest) => Promise<void>;
}

interface UseAddUserFormReturn {
  formState: FormState<CreateUserRequest>;
  formActions: FormActions<CreateUserRequest>;
  roles: any[];
  rolesLoading: boolean;
  roleOptions: Array<{ value: number; label: string; description?: string }>;
  handleSubmit: (data: any) => Promise<void>;
  resetForm: () => void;
}

/**
 * Custom hook for Add User Form
 * Manages form state, validation, data transformation, and role fetching
 */
export const useAddUserForm = (options: UseAddUserFormOptions = {}): UseAddUserFormReturn => {
  const {open, onSuccess, onSubmit} = options;

  // Fetch roles from backend
  const {roles, loading: rolesLoading, fetchRoles} = useRoles();

  // Track if form has been reset for this modal opening
  const hasResetRef = useRef(false);

  // Initialize form state with centralized form utilities
  const [formState, formActions] = useFormState<CreateUserRequest>(
    {
      username: '',
      email: '',
      password: '',
      fullName: '',
      phoneNumber: '',
      roleId: 0,
      status: UserStatus.ACTIVE
    },
    {
      username: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'اسم المستخدم مطلوب';
          }
          if (value.length < 3) {
            return 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل';
          }
          if (!/^[a-zA-Z0-9_]+$/.test(value)) {
            return 'اسم المستخدم يجب أن يحتوي على أحرف وأرقام و _ فقط';
          }
          return null;
        }
      },
      fullName: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'الاسم الكامل مطلوب';
          }
          if (value.length < 2) {
            return 'الاسم الكامل يجب أن يكون حرفين على الأقل';
          }
          return null;
        }
      },
      email: CommonValidationRules.email,
      password: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'كلمة المرور مطلوبة';
          }
          if (value.length < 8) {
            return 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
          }
          return null;
        }
      },
      roleId: {
        required: true,
        customValidator: (value: any) => {
          if (!value || value === 0) {
            return 'الدور مطلوب';
          }
          return null;
        }
      },
      phoneNumber: CommonValidationRules.phone
    }
  );

  // Reset form when modal opens (only once per opening)
  useEffect(() => {
    if (open && !hasResetRef.current) {
      formActions.reset();
      hasResetRef.current = true;
    } else if (!open) {
      hasResetRef.current = false;
    }
  }, [open, formActions]);

  // Fetch roles when modal opens
  useEffect(() => {
    if (open) {
      // Always fetch roles when modal opens to ensure fresh data
      // Similar to RoleAssignmentModal pattern
      fetchRoles().catch(error => {
        // Error is handled by the role store
        // No need to log here as it's handled by StandardizedErrorHandler
      });
    }
  }, [open, fetchRoles]);

  // Set default role when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && formState.data.roleId === 0) {
      // Ensure we only store primitive values
      const roleId = Number(roles[0].id);
      if (!isNaN(roleId)) {
        formActions.setField('roleId', roleId);
      }
    }
  }, [roles, formState.data.roleId, formActions]);

  // Transform roles to options format
  const roleOptions = useMemo(() => {
    return roles.map(role => ({
      value: Number(role.id), // Ensure primitive number
      label: String(role.displayName || role.name), // Ensure primitive string
      description: role.description ? String(role.description) : undefined
    }));
  }, [roles]);

  /**
   * Clean and transform form data before submission
   * Business logic: Data transformation rules
   */
  const cleanFormData = useCallback((data: any): CreateUserRequest => {
    return {
      username: data.username?.toLowerCase().trim() || '',
      fullName: data.fullName?.trim() || '',
      email: data.email?.toLowerCase().trim() || '',
      phoneNumber: data.phoneNumber?.replace(/\s+/g, '') || '',
      password: data.password || '',
      roleId: Number(data.roleId) || 0,
      status: data.status || UserStatus.ACTIVE
    };
  }, []);

  /**
   * Handle form submission
   * Business logic: Data transformation, success handling
   * Note: formActions.submit handles validation and error clearing before calling this
   * This function receives raw form data from formActions.submit
   */
  const handleSubmit = useCallback(async (data: any): Promise<void> => {
    if (!onSubmit) {
      throw new Error('onSubmit callback is required');
    }

    try {
      // Clear any existing errors first (redundant but matches project pattern)
      formActions.clearErrors();

      // Clean and prepare data in camelCase (as CreateUserRequest expects)
      const cleanedData = cleanFormData(data);

      await onSubmit(cleanedData);

      // Call success callback if provided
      onSuccess?.(cleanedData as any);
    } catch (error) {
      // Error notification is handled by StandardizedErrorHandler in the store
      throw error;
    }
  }, [formActions, cleanFormData, onSubmit, onSuccess]);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    formActions.reset();
    hasResetRef.current = false;
  }, [formActions]);

  return {
    formState,
    formActions,
    roles,
    rolesLoading,
    roleOptions,
    handleSubmit,
    resetForm
  };
};

